// Note: the main functions or entry points reside in ./exportPage.ts
import merge from 'deepmerge';
import type { ObjectPageCustomSectionFragment, ObjectPageCustomSectionView } from './controls';
import { exportCustomColumnsToFragment, getCustomColumnPath, deleteColumnFromFragment } from './controls';
import type { NextDefinition } from '../../common';
import {
    getReflectMetadata,
    getChildId,
    convertSectionId,
    getNextTargetDefinition,
    DEFINITION_LINK_PREFIX,
    arrayCombineMerge,
    deleteEmptyStructure,
    fillFlexChangeContent,
    getTargetFromCustomColumnRef,
    arrayIncludes
} from '../../common';
import { getViewExtensionsPath } from '../application';
import { ensureManifestSectionByPathV2, getManifestSectionByPathV2 } from './manifest';
import { isAssociatedCustomSection } from '../import/utils';
import { exportToFlexChange } from '../../common/decoration/flex';
import type { MetadataInstanceInterface } from '../../common/decoration/factory';
import type { TransferParameterTypeV2 } from '../types';
import type { Definition } from 'typescript-json-schema';
import type {
    ExportHandlerFunction,
    ExportResults,
    Manifest,
    ObjectPageSectionData,
    SapOvp,
    SyncRule
} from '@sap/ux-specification-types';
import {
    ChangeIndicator,
    FACETTITLEPREFIX,
    PropertyName,
    ManifestSection,
    PageTypeV2,
    v2,
    ViewTemplateType,
    LogSeverity
} from '@sap/ux-specification-types';
import { log } from '../../../extensionLogger';

const exceptionList = ['colorPalette', 'tabs'];

/**
 * Create missing card from the manifest.
 *
 * @param key - card name
 * @param ovp - OVP section of manifest, to be updated
 */
function createCard(key: string, ovp: SapOvp): void {
    if (!ovp.cards) {
        ovp.cards = {};
    }
    if (!(key in ovp.cards)) {
        ovp.cards[key] = {
            template: ''
        };
    }
}

/**
 * Recursive function that searches for next avaialble id by increasing number by 1.
 *
 * @param {boolean} sectionIds All ids in use.
 * @param {string} candidateId Candidate id to check if it is available.
 * @returns {string} Id string.
 */
function findAvailableId(sectionIds: string[], candidateId: string): string {
    if (sectionIds.includes(candidateId)) {
        const idParts = candidateId.split('|');
        idParts.push('1');
        return findAvailableId(sectionIds, idParts.join('|'));
    }
    return candidateId;
}

/**
 * Private recursive function that traverses the content of the current object. It reads decorators and fills manifest entries and flex changes.
 *
 * @param configObject - current object to traverse
 * @param exportResults - object with all collected manifest entries and flex changes
 * @param jsonSchema - application specific schema
 * @param targetDefinition - the current entry point or definition in the app schema
 * @param {MetadataInstanceInterface} factory - factory for creating metadata instances
 * @param pathHierarchy -  elements of the path indicating the hierarchical level
 * @param {string[]} entityPathArray (optional) - array of elements, describing a path to a subsection of the config.
 *                                                If specified, only elements from this subsection are processed
 */
export function transferManifestEntriesOVP(
    configObject: { [key: string]: any },
    exportResults: ExportResults,
    jsonSchema: Definition,
    targetDefinition: Definition,
    factory: MetadataInstanceInterface,
    pathHierarchy?: string[],
    entityPathArray?: string[]
): void {
    if (targetDefinition?.properties) {
        processProperties(
            configObject,
            exportResults,
            jsonSchema,
            targetDefinition.properties,
            factory,
            pathHierarchy,
            entityPathArray
        );
    }
}

/**
 * Assign sync rules to {currentObject}.
 *
 * @param {MetadataInstanceInterface} factory - factory for metadata
 * @param {string} structureName - name of the complex structure in schema
 * @param {object} currentObject - the given part of the configuration
 * @param {string} defaultTypeName - name of the default type that shall be taken if no class is registered for structureName
 * @returns {object} the currentObject, with the reflect-metadata assigned
 */
export function assignReflectMetadataRules(
    factory: MetadataInstanceInterface,
    structureName: string,
    currentObject: object,
    defaultTypeName: string
): object {
    return factory.createInstance(PageTypeV2.OverviewPage, defaultTypeName, currentObject, structureName);
}

type ProcessDefinitionParams = {
    pathHierarchy: string[];
    propertyList: object;
    appSchema: Definition;
    currentObject: object;
    configObject: { [key: string]: any };
    factory: MetadataInstanceInterface;
    exportResults: ExportResults;
    entityPathArray?: string[];
};

/**
 * Processes anyOf definitions of the schema while exporting an OVP config.
 *
 * @param {ProcessDefinitionParams} params - structure comprising all information needed for processing
 * @returns the params.currentObject, with the reflect-metadata assigned
 */
function processAnyOfDefinitionsOfOVP(params: ProcessDefinitionParams): object {
    params.pathHierarchy.pop();
    let newConfigObject = params.currentObject;
    for (const anyOfKey in params.propertyList['anyOf']) {
        if (params.propertyList['anyOf'][anyOfKey].$ref) {
            const definitionArray = params.propertyList['anyOf'][anyOfKey].$ref.split(DEFINITION_LINK_PREFIX);
            const nextTargetDefinition = params.appSchema['definitions'][definitionArray[1]];
            newConfigObject = assignReflectMetadataRules(
                params.factory,
                definitionArray[1],
                newConfigObject,
                'CustomCard'
            );
            transferManifestEntriesOVP(
                newConfigObject,
                params.exportResults,
                params.appSchema,
                nextTargetDefinition as Definition,
                params.factory,
                [...params.pathHierarchy]
            );
        }
    }
    return newConfigObject;
}

/**
 * Checks and applies the synchronization rule for exporting a configuration object to the manifest.
 *
 * @param exportHandler - A function or boolean indicating how the export should be handled.
 * @param manifestSection - The section of the manifest to be updated.
 * @param configObject - The configuration object containing the data to be exported.
 * @param key - The key of the property being processed.
 * @param currentObject - The current object being processed for export.
 */
function checkSyncRuleForExport(
    exportHandler: boolean | ExportHandlerFunction,
    manifestSection: object,
    configObject: { [key: string]: any },
    key: string,
    currentObject: object
) {
    if (exportHandler && typeof exportHandler === 'function') {
        exportHandler(manifestSection, configObject, key);
    } else if (typeof currentObject === 'object' && !exceptionList.includes(key)) {
        // colorPalette is an exception as it can contain anything as per our schema, so just replace the object
        manifestSection[key] = merge(manifestSection[key] || {}, currentObject || {}, {
            arrayMerge: arrayCombineMerge
        });
    } else {
        manifestSection[key] = currentObject;
    }
}

/**
 * Transfer manifest settings from config to manifest.
 *
 * @param syncRule - metadata rule used for import and export
 * @param pathHierarchy -  elements of the path indicating the hierarchical level
 * @param key - The key of the property being processed.
 * @param exportResults - overall result list, to be updated
 * @param currentObject - The current object being processed for export
 * @param configObject - The configuration object containing the data to be processed.
 */
function transferToManifest(
    syncRule: SyncRule,
    pathHierarchy: string[],
    key: string,
    exportResults: ExportResults,
    currentObject: object,
    configObject: { [key: string]: any }
) {
    const path = syncRule.manifest.path([...pathHierarchy, key]);
    const manifestSection = ensureManifestSectionByPathV2(exportResults.manifest, path);
    if (key in v2.CardSettingsType || key === 'settings') {
        manifestSection['settings'] = merge(manifestSection['settings'] || {}, currentObject || {}, {
            arrayMerge: arrayCombineMerge
        });
    } else if (currentObject !== undefined) {
        const exportHandler = syncRule.manifest.export;
        checkSyncRuleForExport(exportHandler, manifestSection, configObject, key, currentObject);
    } else if (manifestSection[key] !== undefined) {
        delete manifestSection[key];
    }
}

/**
 * Transfers cards from the configuration object to the manifest and updates the export results.
 *
 * @param params - Structure comprising all information needed for processing cards.
 */
function transferCards(params: ProcessDefinitionParams) {
    Object.keys(params.currentObject).forEach((cardKey) => {
        const cardType = params.currentObject[cardKey].template ?? 'CustomCard';
        const cardTypes = [...new Set(Object.keys(v2.cardTemplateTypeMap))].filter(
            (type) => type !== cardType
        ) as v2.CardTemplateType[];
        // Ensure cardTemplate is moved to the end
        cardTypes.push(cardType);
        for (const variantCardType of cardTypes) {
            //Determine target defintion
            let targetDefinition;
            if (!Object.values(v2.CardTemplateType).includes(variantCardType)) {
                targetDefinition = ['CustomCard'];
            } else {
                targetDefinition = Object.keys(params.appSchema.definitions).filter((definition) => {
                    const template = (params.appSchema.definitions[definition] as Definition).properties
                        ?.template as Definition;
                    if (template?.enum) {
                        return template.enum.indexOf(variantCardType) > -1;
                    } else if (template?.const) {
                        return template.const === variantCardType;
                    }
                });
            }
            //Get metadata
            params.currentObject[cardKey] = assignReflectMetadataRules(
                params.factory,
                targetDefinition[0],
                params.currentObject[cardKey],
                'CustomCard'
            );
            createCard(cardKey, params.exportResults.manifest[ManifestSection.ovp]);
            params.exportResults.manifestChangeIndicator = ChangeIndicator.Updated;
            transferManifestEntriesOVP(
                params.currentObject[cardKey],
                params.exportResults,
                params.appSchema,
                params.appSchema.definitions[targetDefinition[0]] as Definition,
                params.factory,
                ['cards', cardKey],
                params.entityPathArray
            );
        }
    });
    //check for deletions:
    Object.keys(params.exportResults.manifest[ManifestSection.ovp].cards).forEach((manifestCard) => {
        if (!params.currentObject[manifestCard]) {
            delete params.exportResults.manifest[ManifestSection.ovp].cards[manifestCard];
            params.exportResults.manifestChangeIndicator = ChangeIndicator.Updated;
        }
    });
}

/**
 * Export of properties of entity of type object.
 *
 * @param {ProcessDefinitionParams} params  - Input parameters
 * @param {string} propertyKey - Property key
 */
function exportOfObject(params: ProcessDefinitionParams, propertyKey: string): void {
    if (propertyKey === 'cards') {
        //inserts and updates:
        transferCards(params);
    } else {
        const nextTargetDefinition = getNextTargetDefinition(
            params.appSchema,
            '',
            params.currentObject,
            params.propertyList[propertyKey],
            propertyKey,
            params.factory,
            PageTypeV2.OverviewPage
        );
        if (params.propertyList[propertyKey].$ref) {
            const definitionArray = params.propertyList[propertyKey].$ref.split(DEFINITION_LINK_PREFIX);

            params.currentObject = assignReflectMetadataRules(
                params.factory,
                propertyKey,
                params.currentObject,
                definitionArray[1]
            );
            params.pathHierarchy = [...params.pathHierarchy, propertyKey];
            transferManifestEntriesOVP(
                params.currentObject,
                params.exportResults,
                params.appSchema,
                nextTargetDefinition.targetDefinition,
                params.factory,
                params.pathHierarchy
            );
        } else if (params.propertyList[propertyKey].anyOf) {
            params.pathHierarchy = [...params.pathHierarchy, propertyKey, 'anyOf'];
            processProperties(
                params.configObject,
                params.exportResults,
                params.appSchema,
                params.propertyList[propertyKey],
                params.factory,
                params.pathHierarchy,
                params.entityPathArray
            );
        } else if (params.propertyList[propertyKey].properties !== undefined) {
            params.pathHierarchy = [...params.pathHierarchy, propertyKey];
            processProperties(
                params.configObject[propertyKey],
                params.exportResults,
                params.appSchema,
                params.propertyList[propertyKey].properties,
                params.factory,
                params.pathHierarchy,
                params.entityPathArray
            );
        }
    }
}

/**
 * Recursive function that transfers the content of a list of properties of a configuration to the overall exportResults.
 *
 * @param configObject - the given part of the configuration
 * @param exportResults - overall result list, to be updated
 * @param appSchema - app-specific JSON schema
 * @param propertyList - current property list of the config (properties or additional properties)
 * @param {MetadataInstanceInterface} factory - factory for creating metadata instances
 * @param pathHierarchy -  elements of the path indicating the hierarchical level
 * @param {string[]} entityPathArray (optional) - array of elements, describing a path to a subsection of the config.
 *                                                If specified, only elements from this subsection are processed
 */
function processProperties(
    configObject: { [key: string]: any },
    exportResults: ExportResults,
    appSchema: Definition,
    propertyList: object,
    factory: MetadataInstanceInterface,
    pathHierarchy?: string[],
    entityPathArray?: string[]
): void {
    for (const propertyKey in propertyList) {
        if (propertyKey === 'description') {
            continue;
        }
        if (entityPathArray) {
            if (
                pathHierarchy &&
                entityPathArray[pathHierarchy.length] !== propertyKey &&
                !arrayIncludes(pathHierarchy, entityPathArray) &&
                pathHierarchy[pathHierarchy.length - 1] !== 'anyOf'
            ) {
                continue;
            }
        }
        let currentObject = configObject[propertyKey];
        const syncRule = getReflectMetadata(configObject, propertyKey);

        const processDefinitionParams: ProcessDefinitionParams = {
            pathHierarchy,
            propertyList,
            appSchema,
            currentObject,
            configObject,
            factory,
            exportResults,
            entityPathArray
        };
        if (pathHierarchy[pathHierarchy.length - 1] === 'anyOf') {
            processDefinitionParams.currentObject = configObject[pathHierarchy[pathHierarchy.length - 2]];
            currentObject = processAnyOfDefinitionsOfOVP(processDefinitionParams);
        } else if (currentObject && typeof currentObject === 'object' && propertyList[propertyKey]) {
            exportOfObject(processDefinitionParams, propertyKey);
        }
        if (syncRule?.manifest) {
            transferToManifest(syncRule, pathHierarchy, propertyKey, exportResults, currentObject, configObject);
        }
    }
}

/**
 * Evaluates an export rule for a property, transfers to flex change.
 *
 * @param transferParameters - list of input parameters for the recursive function call
 * @param breadcrumbs - list of breadcrumbs, i.e. ID parts for building the stable ID
 * @param syncRule - export rule from the object classes decorator
 * @param ids - list of parent & child ids
 * @param title - title from the app schema (comprising for instance the facet ID)
 * @param configObject - current (sub)object of the configuration file
 * @param key - key of the given property
 */
function evaluateFlexRule(
    transferParameters: TransferParameterTypeV2,
    breadcrumbs: string[],
    syncRule: SyncRule,
    ids: string[],
    title: string,
    configObject: object,
    key: string
): void {
    let localBreadcrumbs: string[];

    if (breadcrumbs.length === 0) {
        //only added on top = page level, do not forward
        localBreadcrumbs = ['page'];
    } else {
        localBreadcrumbs = breadcrumbs;
    }
    const controlId = syncRule.flex.controlId(
        transferParameters.baseId,
        ids,
        localBreadcrumbs,
        syncRule.flex.controlType,
        title
    );
    const flexChange = fillFlexChangeContent(configObject, syncRule, transferParameters.ui5Version, key, controlId);
    const localManifest = JSON.parse(JSON.stringify(transferParameters.exportResults.manifest)) as Manifest;
    localManifest['sap.app'].id = transferParameters.appId;
    const completeFlexChange = exportToFlexChange(flexChange, localManifest);
    const flexChangeExists = transferParameters.exportResults.flexChanges.find((change) => {
        let result = false,
            oldChange: object = JSON.parse(change);
        oldChange = {
            content: oldChange['content'],
            selector: oldChange['selector']
        };
        let newChange: object = JSON.parse(completeFlexChange);
        newChange = {
            content: newChange['content'],
            selector: newChange['selector']
        };
        if (JSON.stringify(oldChange) === JSON.stringify(newChange)) {
            result = true;
        }
        return result;
    });
    if (!flexChangeExists) {
        transferParameters.exportResults.flexChanges.push(completeFlexChange);
    }
}

/**
 * Exports a given config property value to manifest.
 *
 * @param {TransferParameterTypeV2} transferParameters - list of input parameters for the recursive function call
 * @param syncRule - export rule from the object classes decorator
 * @param manifestSection - current section in manifest.json
 * @param configObject - current (sub)object of the configuration file
 * @param key - key of the given property in the config
 * @param manifestKey - key of the given property in manifest
 */
function exportConfigValueToManifest(
    transferParameters: TransferParameterTypeV2,
    syncRule: SyncRule,
    manifestSection: object,
    configObject: object,
    key: string,
    manifestKey: string
) {
    if (
        // In case of arrays we take over complete array
        !(
            transferParameters.propertyDefinition['type'] !== 'array' &&
            transferParameters.nextDefinition &&
            transferParameters.nextDefinition['targetDefinition'].type === 'object' &&
            transferParameters.nextDefinition['targetDefinition'].properties
        )
    ) {
        const exportHandler = syncRule.manifest.export;
        if (exportHandler && typeof exportHandler === 'function') {
            exportHandler(manifestSection, configObject, key, manifestKey);
        } else if (exportHandler !== false) {
            manifestSection[manifestKey] = configObject[key];
        }
        transferParameters.exportResults.manifestChangeIndicator = ChangeIndicator.Updated;
    } else if (
        transferParameters.propertyDefinition['type'] !== 'array' &&
        manifestSection[manifestKey] === undefined
    ) {
        manifestSection[manifestKey] = {};
        transferParameters.exportResults.manifestChangeIndicator = ChangeIndicator.Updated;
    }
}

/**
 * Evaluates an export rule for a property, transfers to manifest setting.
 *
 * @param transferParameters - list of input parameters for the recursive function call
 * @param path - The path to the manifest section or configuration being processed.
 * @param syncRule - export rule from the object classes decorator
 * @param key - key of the given property
 * @param configObject - current (sub)object of the configuration file
 */
function evaluateManifestRule(
    transferParameters: TransferParameterTypeV2,
    path: string,
    syncRule: SyncRule,
    key: string,
    configObject: object
) {
    const manifestSection = ensureManifestSectionByPathV2(transferParameters.exportResults.manifest, path);
    const manifestKey = syncRule.manifest.key || key;
    if (configObject[key] !== undefined) {
        exportConfigValueToManifest(transferParameters, syncRule, manifestSection, configObject, key, manifestKey);
    } else {
        //delete value
        const deleteHandler = syncRule.manifest.delete;
        if (deleteHandler && typeof deleteHandler === 'function') {
            deleteHandler(manifestSection, configObject, key, manifestKey);
        } else if (Object.prototype.hasOwnProperty.call(manifestSection, manifestKey)) {
            delete manifestSection[manifestKey];
        }
        transferParameters.exportResults.manifestChangeIndicator = ChangeIndicator.Updated;
        deleteEmptyStructure(
            transferParameters.exportResults.manifest,
            path,
            transferParameters.deletionRequest,
            transferParameters.entityPathArray
        );
    }
}

/**
 * Determines the manifest path for sections based on the provided parameters.
 *
 * @param breadcrumbs - Array of breadcrumb strings representing the hierarchical structure.
 * @param path - The current path to the manifest section.
 * @param syncRule - The synchronization rule used for exporting the configuration.
 * @param transferParameters - Parameters required for transferring manifest entries and flex changes.
 * @param title - The title of the section or configuration being processed.
 * @returns {string} The computed manifest path for the given section.
 */
function getManifestPathOfSections(
    breadcrumbs: string[],
    path: string,
    syncRule: SyncRule,
    transferParameters: TransferParameterTypeV2,
    title: string
) {
    if (breadcrumbs[1] === 'custom') {
        path = syncRule.manifest.path(transferParameters.pageKeys, {} as Manifest, breadcrumbs[2]);
    } else {
        path = syncRule.manifest.path(
            transferParameters.pageKeys,
            {} as Manifest,
            title?.startsWith(FACETTITLEPREFIX) ? title.split(FACETTITLEPREFIX)[1] : breadcrumbs[1],
            breadcrumbs[breadcrumbs.length - 1]
        );
        path = path.replace('/@', '::').replace(/VOCWITHCOLONS/g, '::com.sap.vocabularies');
    }
    return path;
}

/**
 * Determines the manifest path for a table based on the provided parameters.
 *
 * @param breadcrumbs - Array of breadcrumb strings representing the hierarchical structure.
 * @param path - The current path to the manifest section or configuration being processed.
 * @param syncRule - The synchronization rule used for exporting the configuration.
 * @param transferParameters - Parameters required for transferring manifest entries and flex changes.
 * @param configObject - The configuration object containing the data to be processed.
 * @returns {string} The computed manifest path for the given table.
 */
function getManifestPathOfTable(
    breadcrumbs: string[],
    path: string,
    syncRule: SyncRule,
    transferParameters: TransferParameterTypeV2,
    configObject: object
) {
    if (breadcrumbs[1] === 'columns' && breadcrumbs[2] === 'custom') {
        path = syncRule.manifest.path(
            transferParameters.pageKeys,
            transferParameters.exportResults.manifest,
            configObject['tabKey']
        );
    } else {
        path = syncRule.manifest.path(transferParameters.pageKeys, {} as Manifest, breadcrumbs[breadcrumbs.length - 1]);
    }
    return path;
}

/**
 * Determines the path to a manifest setting, based on the syncRule.
 *
 * @param {TransferParameterTypeV2} transferParameters - list of input parameters for the recursive function call
 * @param breadcrumbs - list of breadcrumbs, i.e. ID parts for building the stable ID
 * @param {SyncRule} syncRule - export rule from the object classes decorator
 * @param configObject - current (sub)object of the configuration file
 * @param title - title from the app schema (comprising for instance the facet ID)
 * @returns the path to the manifest setting
 */
function getManifestPath(
    transferParameters: TransferParameterTypeV2,
    breadcrumbs: string[],
    syncRule: SyncRule,
    configObject: object,
    title: string
): string {
    let path: string;
    if (breadcrumbs[0] === PropertyName.sections) {
        path = getManifestPathOfSections(breadcrumbs, path, syncRule, transferParameters, title);
    } else if (breadcrumbs[0] === PropertyName.table) {
        path = getManifestPathOfTable(breadcrumbs, path, syncRule, transferParameters, configObject);
    } else if (breadcrumbs[0] === 'keyPerformanceIndicators') {
        path = syncRule.manifest.path(transferParameters.pageKeys, transferParameters.exportResults.manifest, title);
    } else if (breadcrumbs[0] === 'filterBar' && breadcrumbs[1] === 'selectionFields') {
        path = syncRule.manifest.path(
            transferParameters.pageKeys,
            transferParameters.exportResults.manifest,
            breadcrumbs.slice(2).join('/')
        );
    } else {
        path = syncRule.manifest.path(transferParameters.pageKeys);
    }
    return path;
}

/**
 * Evaluates an export rule for a property or object, transfers to flex change or manifest setting.
 *
 * @param {TransferParameterTypeV2} transferParameters - list of input parameters for the recursive function call
 * @param {SyncRule} syncRule - export rule from the object classes decorator
 * @param configObject - current (sub)object of the configuration file
 * @param breadcrumbs - list of breadcrumbs, i.e. ID parts for building the stable ID
 * @param ids - list of parent & child ids
 * @param title - title from the app schema (comprising for instance the facet ID)
 * @param key - key of the given property
 */
function evaluateExportRule(
    transferParameters: TransferParameterTypeV2,
    syncRule: SyncRule,
    configObject: object,
    breadcrumbs: string[],
    ids: string[],
    title: string,
    key: string
): void {
    if (!syncRule) {
        return;
    }
    //eliminate subsections' hierarchy
    let index = breadcrumbs.indexOf('subsections');
    while (index > -1) {
        breadcrumbs.splice(index - 1, 2);
        index = breadcrumbs.indexOf('subsections');
    }

    if (syncRule.flex) {
        evaluateFlexRule(transferParameters, breadcrumbs, syncRule, ids, title, configObject, key);
    } else if (syncRule.manifest) {
        const path = getManifestPath(transferParameters, breadcrumbs, syncRule, configObject, title);
        evaluateManifestRule(transferParameters, path, syncRule, key, configObject);
    }
}

type ColumnParams = {
    path: string;
    exportResults: ExportResults;
    entitySet: string;
    config: v2.TableCustomColumns;
    sectionKey: string;
    extensionType: string;
    appId: string;
};

/**
 * Deletes an obsolete custom column from manifest.
 *
 * @param key - The unique identifier for the column to be deleted.
 * @param columnParams - Parameters containing details about the column and its context.
 * @param manifestSection - The section of the manifest where the column is defined.
 */
function deleteObsoleteColumn(key: string, columnParams: ColumnParams, manifestSection: object) {
    const keyParts = key.split('|');
    const keyEntitySet = keyParts[1];
    const keyTab = keyParts[2];
    if (keyEntitySet === columnParams.entitySet) {
        //relevant extension. still present in config?
        const existingConfigEntries = columnParams.config?.filter(
            (column) =>
                (column.tabKey === keyTab ||
                    (keyTab === undefined && column.tabKey === '') ||
                    (keyTab && columnParams.sectionKey === keyTab)) &&
                column.extensionType === columnParams.extensionType
        );
        if (!existingConfigEntries || existingConfigEntries.length === 0) {
            deleteColumnFromFragment(manifestSection, key, columnParams.appId, columnParams.exportResults.fragments);
            // delete from manifest
            delete manifestSection[key];
            // delete cells entry
            if (key.startsWith(ViewTemplateType.ResponsiveTableColumnsExtension)) {
                const cellsPattern = key.replace(
                    ViewTemplateType.ResponsiveTableColumnsExtension,
                    ViewTemplateType.ResponsiveTableCellsExtension
                );
                deleteColumnFromFragment(
                    manifestSection,
                    cellsPattern,
                    columnParams.appId,
                    columnParams.exportResults.fragments
                );
                // delete from manifest
                delete manifestSection[cellsPattern];
            }
        }
    }
}

/**
 * Walks through the extensions registered in manifest and compares the columns of the fragment with the ones present in config.
 * If not present anymore, the extension column gets deleted from fragment.
 *
 * @param columnParams - Parameters containing details about the column and its context.
 * @param columnParams.path - path to extensions section in manifest
 * @param columnParams.exportResults - result structure, including the fragments that may get updated
 * @param columnParams.entitySet - current entity set
 * @param columnParams.config - current config JSON
 * @param columnParams.sectionKey - section key
 * @param columnParams.extensionType - extensionType
 * @param columnParams.appId - application ID
 */
function deleteObsoleteColumnsFromFragment(columnParams: ColumnParams) {
    const folderPath = columnParams.path.substring(0, columnParams.path.lastIndexOf('/'));
    const manifestSection = getManifestSectionByPathV2(columnParams.exportResults.manifest, folderPath);
    const extensionPattern = columnParams.path.substring(columnParams.path.lastIndexOf('/') + 1);
    for (const key in manifestSection) {
        if (key.startsWith(extensionPattern)) {
            columnParams.exportResults.manifestChangeIndicator = ChangeIndicator.Updated;
            deleteObsoleteColumn(key, columnParams, manifestSection);
        }
    }
}

/**
 * Clusters the custom columns if a config by the extension's path in manifest.
 *
 * @param customColumnsInConfig - list of all custom columns in config
 * @param schemaDefinition - schema definition of the table custom column
 * @param v2Page - current page in manifest
 * @param entitySet - current entity set
 * @param sectionKey key of the section (in case of OP)
 * @returns the custom columns, clustered by the manifest path
 */
function clusterConfigColumnsByPath(
    customColumnsInConfig: v2.TableCustomColumns,
    schemaDefinition: object,
    v2Page: v2.SapUiAppPageV2,
    entitySet: string,
    sectionKey: string
): object {
    const cluster = {};
    for (const customColumn of customColumnsInConfig) {
        //verify that mandatory parameters are filled
        if (
            !customColumn['className'] ||
            !customColumn['fragmentName'] ||
            !customColumn['columnKey'] ||
            !customColumn['extensionType']
        ) {
            continue;
        }
        //check fragment name
        if (schemaDefinition['properties']['fragmentName']['pattern']) {
            const namePattern = new RegExp(schemaDefinition['properties']['fragmentName']['pattern']);
            if (namePattern.test(customColumn['fragmentName']) === false) {
                continue;
            }
        }
        const { path, viewTemplateType } = getCustomColumnPath(
            v2Page,
            entitySet,
            customColumn.tabKey || sectionKey,
            customColumn['extensionType']
        );

        if (!cluster[path]) {
            cluster[path] = {
                viewTemplateType: viewTemplateType,
                columns: []
            };
        }
        cluster[path].columns.push(customColumn);
    }
    return cluster;
}

/**
 * Transfers column extensions to manifest and fragment files.
 *
 * @param transferParameters - list of input parameters for the recursive function call
 * @param customColumnsInConfig - config part comprising the table custom columns
 * @param schemaDefinition - Column schema definition
 * @param [objectPageSectionData] - Object Page section's data containing target and key options.
 */
function transferColumnExtensions(
    transferParameters: TransferParameterTypeV2,
    customColumnsInConfig: v2.TableCustomColumns,
    schemaDefinition: object,
    objectPageSectionData?: ObjectPageSectionData
): void {
    if (!transferParameters.v2Page) {
        return;
    }
    const sectionKey = objectPageSectionData ? objectPageSectionData.key : undefined;
    const sectionTarget = objectPageSectionData ? objectPageSectionData.target : undefined;
    const entitySet = sectionTarget || transferParameters.v2Page.entitySet;
    for (const extensionType in v2.TableColumnExtensionTypeV2) {
        const { path } = getCustomColumnPath(transferParameters.v2Page, entitySet, sectionKey, extensionType);
        if (!path) {
            continue;
        }
        const columParams = {
            path,
            exportResults: transferParameters.exportResults,
            entitySet,
            config: customColumnsInConfig,
            sectionKey,
            extensionType,
            appId: transferParameters.appId
        };
        try {
            deleteObsoleteColumnsFromFragment(columParams);
        } catch (error) {
            log(transferParameters.logger, { severity: LogSeverity.Error, message: error.message });
        }
    }
    if (customColumnsInConfig) {
        //cluster existing custom columns by manifest path (or tab key)
        const cluster = clusterConfigColumnsByPath(
            customColumnsInConfig,
            schemaDefinition,
            transferParameters.v2Page,
            entitySet,
            sectionKey
        );
        //Export to fragment
        for (const path in cluster) {
            exportCustomColumnsToFragment(
                transferParameters,
                path,
                cluster[path].columns,
                cluster[path].viewTemplateType
            );
        }
    }
}

/**
 * Recursive function that traverses the content of the current object. It reads decorators and fills manifest entries and flex changes.
 *
 * @param {TransferParameterTypeV2} transferParameters - list of input parameters for the recursive function call
 * @param configSections - sections from configuration object
 * @param pageKey  - page key
 * @param parentIds - array of collected parent ids
 * @param breadcrumbs - array of properties that we are currently processing. Required for instance for table column name
 * @param targetDefinition - the current entry point or definition in the app schema
 */
function transferManifestExtensions(
    transferParameters: TransferParameterTypeV2,
    configSections: (ObjectPageCustomSectionFragment | ObjectPageCustomSectionView)[],
    pageKey: string,
    parentIds: string[],
    breadcrumbs: string[],
    targetDefinition: object
): void {
    const sectionIds: string[] = [];
    const entity = pageKey.split('|')[1];
    if (!configSections) {
        return;
    }
    for (const section of configSections) {
        const id = findAvailableId(sectionIds, [section.relativePosition, entity, section.relatedFacet].join('|'));
        sectionIds.push(id);
    }
    for (let index = 0; index < sectionIds.length; index++) {
        const definition = targetDefinition['anyOf'].find((element) =>
            element.$ref?.includes(configSections[index].constructor.name)
        );
        const definitionArray = definition.$ref.split(DEFINITION_LINK_PREFIX);
        const nextTargetDefinition =
            transferParameters.appSchema['definitions'][definitionArray[definitionArray.length - 1]];

        transferManifestEntriesAndFlexChange(
            transferParameters,
            configSections[index],
            parentIds,
            [...breadcrumbs, sectionIds[index]],
            nextTargetDefinition
        );
    }
    // Delete removed manifest entries
    const manifestSections = ensureManifestSectionByPathV2(
        transferParameters.exportResults.manifest,
        getViewExtensionsPath()
    );
    for (const key in manifestSections) {
        if (isAssociatedCustomSection(key, entity) && !sectionIds.includes(key)) {
            delete manifestSections[key];
        }
    }
}

/**
 * Method returns converted section id by resolving passed section id and title from schema.
 *
 * @param {string[]} breadcrumbs Array of properties that we are currently processing. Required for instance for table column name.
 * @param {string} title Title from JSON schema, comprising the facet ID
 * @returns {string | undefined} Resolved section id.
 */
function getSectionId(breadcrumbs: string[], title: string): string | undefined {
    let sectionId = undefined;
    for (let i = breadcrumbs.length - 1; i >= 0; i--) {
        if (breadcrumbs[i] === PropertyName.table) {
            sectionId = breadcrumbs[i - 1];
            break;
        }
    }
    return convertSectionId(sectionId, title);
}

/**
 * Processes index tables in schema.
 *
 * @param currentConfigObject - The current configuration object being processed.
 * @param transferParameters - Parameters required for transferring manifest entries and flex changes.
 * @param title - The title of the section or configuration being processed.
 * @param key - The key of the property being processed.
 * @param ids - An array of collected parent and child IDs.
 * @param breadcrumbs - An array of properties that represent the hierarchical structure being processed.
 */
function transferIndexTable(
    currentConfigObject: object,
    transferParameters: TransferParameterTypeV2,
    title: string,
    key: string,
    ids: string[],
    breadcrumbs: string[]
) {
    const propertyDefinition = transferParameters.propertyDefinition as Definition;
    if (!currentConfigObject || !propertyDefinition?.additionalProperties) {
        return;
    }
    Object.keys(currentConfigObject).forEach((newKey) => {
        const nextDefinition = getNextTargetDefinition(
            transferParameters.appSchema,
            title,
            currentConfigObject[newKey],
            propertyDefinition.additionalProperties as Definition,
            key,
            transferParameters.factory,
            transferParameters.pageType
        );

        transferManifestEntriesAndFlexChange(
            transferParameters,
            nextDefinition.configObject,
            ids,
            [...breadcrumbs, key],
            nextDefinition.targetDefinition,
            newKey
        );
    });
}

/**
 * Transfers custom columns of schema.
 *
 * @param propertyDefinition - The definition of the property being processed.
 * @param breadcrumbs - An array of properties representing the hierarchical structure being processed.
 * @param title - The title of the section or configuration being processed.
 * @param transferParameters - Parameters required for transferring manifest entries and flex changes.
 * @param configObject - The current configuration object being processed.
 * @param nextDefinition - The next definition in the schema.
 */
function transferCustomColumns(
    propertyDefinition: any,
    breadcrumbs: string[],
    title: string,
    transferParameters: TransferParameterTypeV2,
    configObject: { [key: string]: any },
    nextDefinition: NextDefinition
) {
    let objectPageSectionData: ObjectPageSectionData | undefined;
    const target = getTargetFromCustomColumnRef(propertyDefinition.$ref);
    if (target) {
        objectPageSectionData = {
            target,
            key: getSectionId(breadcrumbs, title)
        };
    }
    transferColumnExtensions(
        transferParameters,
        configObject?.custom as v2.TableCustomColumns,
        nextDefinition.targetDefinition,
        objectPageSectionData
    );
}

/**
 * Processes anyOf definitions of schema.
 *
 * @param propertyDefinition - The definition of the property being processed.
 * @param transferParameters - Parameters required for transferring manifest entries and flex changes.
 * @param title - The title of the section or configuration being processed.
 * @param currentConfigObject - The current configuration object being processed.
 * @param key - The key of the property being processed.
 * @param ids - An array of collected parent and child IDs.
 * @param breadcrumbs - An array of properties that represent the hierarchical structure being processed.
 */
function handleAnyOfDefinitions(
    propertyDefinition: any,
    transferParameters: TransferParameterTypeV2,
    title: string,
    currentConfigObject: object,
    key: string,
    ids: string[],
    breadcrumbs: string[]
) {
    propertyDefinition['anyOf'].forEach((element) => {
        const nextDefinition = getNextTargetDefinition(
            transferParameters.appSchema,
            title,
            currentConfigObject,
            element,
            key,
            transferParameters.factory,
            transferParameters.pageType
        );
        if (nextDefinition) {
            transferManifestEntriesAndFlexChange(
                transferParameters,
                nextDefinition.configObject,
                ids,
                [...breadcrumbs, key],
                nextDefinition.targetDefinition,
                nextDefinition.title
            );
        }
    });
}

/**
 * Processes complex definitions of schema.
 *
 * @param transferParameters - Parameters required for transferring manifest entries and flex changes.
 * @param breadcrumbs - An array of properties representing the hierarchical structure being processed.
 * @param configObject - The current configuration object being processed.
 * @param ids - An array of collected parent and child IDs.
 * @param propertyKey - The key of the property being processed.
 * @param nextDefinition - The next definition in the schema.
 * @param title - The title of the section or configuration being processed.
 */
function transferComplexProperty(
    transferParameters: TransferParameterTypeV2,
    breadcrumbs: string[],
    configObject: { [key: string]: any },
    ids: string[],
    propertyKey: string,
    nextDefinition: NextDefinition,
    title?: string
): void {
    const propertyDefinition = transferParameters.propertyDefinition as Definition;
    if (
        propertyDefinition?.type === 'array' &&
        (breadcrumbs[0] === 'sections' || breadcrumbs[0] === 'subsections') &&
        propertyKey === 'custom'
    ) {
        transferManifestExtensions(
            transferParameters,
            configObject[propertyKey],
            transferParameters.pageKeys[transferParameters.pageKeys.length - 1],
            ids,
            [...breadcrumbs, propertyKey],
            propertyDefinition?.items as Definition
        );
    } else if (nextDefinition && propertyKey === 'custom') {
        transferCustomColumns(propertyDefinition, breadcrumbs, title, transferParameters, configObject, nextDefinition);
        return;
    } else if (nextDefinition) {
        transferManifestEntriesAndFlexChange(
            transferParameters,
            nextDefinition.configObject,
            ids,
            [...breadcrumbs, propertyKey],
            nextDefinition.targetDefinition,
            nextDefinition.title
        );
    } else if (propertyDefinition['anyOf']) {
        handleAnyOfDefinitions(
            propertyDefinition,
            transferParameters,
            title,
            configObject?.[propertyKey],
            propertyKey,
            ids,
            breadcrumbs
        );
    }
}

/**
 * Function that traverses the content of the current object. It reads decorators and fills manifest entries and flex changes.
 *
 * @param {TransferParameterTypeV2} transferParameters - list of input parameters for the recursive function call
 * @param configObject - current (sub)object of the configuration file
 * @param parentIds - array of collected parent ids
 * @param breadcrumbs - array of properties that we are currently processing. Required for instance for table column name
 * @param schemaDefinition - the current entry point or definition in the app schema
 * @param title - title from the app schema (comprising for instance the facet ID)
 */
export function transferManifestEntriesAndFlexChange(
    transferParameters: TransferParameterTypeV2,
    configObject: { [key: string]: any },
    parentIds: string[],
    breadcrumbs: string[],
    schemaDefinition: Definition,
    title?: string
): void {
    let currentConfigObject: object;

    if (!schemaDefinition?.properties) {
        return;
    }
    for (const propertyKey in schemaDefinition.properties) {
        if (transferParameters.entityPathArray) {
            if (
                transferParameters.entityPathArray[breadcrumbs.length] !== propertyKey &&
                !arrayIncludes(breadcrumbs, transferParameters.entityPathArray)
            ) {
                continue;
            }
        }
        currentConfigObject = configObject?.[propertyKey];
        const childId = configObject && getChildId(configObject, propertyKey);
        const ids = childId ? [...parentIds, childId] : parentIds;
        const syncRule = getReflectMetadata(configObject, propertyKey);
        const propertyDefinition = (transferParameters.propertyDefinition = schemaDefinition.properties[
            propertyKey
        ] as Definition);
        const nextDefinition = (transferParameters.nextDefinition = getNextTargetDefinition(
            transferParameters.appSchema,
            title,
            currentConfigObject,
            propertyDefinition,
            propertyKey,
            transferParameters.factory,
            transferParameters.pageType
        ));
        //Evaluate sync rule for the export
        evaluateExportRule(transferParameters, syncRule, configObject, [...breadcrumbs], ids, title, propertyKey);
        //index table
        transferIndexTable(currentConfigObject, transferParameters, title, propertyKey, ids, breadcrumbs);

        if (currentConfigObject === undefined || typeof currentConfigObject === 'object') {
            transferComplexProperty(
                transferParameters,
                breadcrumbs,
                configObject,
                ids,
                propertyKey,
                nextDefinition,
                title
            );
        } else if (propertyDefinition?.$ref === DEFINITION_LINK_PREFIX + 'TableCustomColumns') {
            //empty config ==> delete all given extensions
            transferColumnExtensions(
                transferParameters,
                [],
                transferParameters.appSchema['definitions']['TableCustomColumn']
            );
            continue;
        }
    }
}
